import { DateTime } from 'luxon';
import { useMemo } from 'react';
import { Link } from 'react-router-dom-v5-compat';
import styled from 'styled-components';

import HoverCopyableField from 'dashboard/components/HoverCopyableField';
import { MonoSpan } from 'dashboard/components/Typography';
import TableLayout, {
  TableLinkTR,
  TableNullState,
  TableTD,
  TableTH,
  TableTR,
} from 'dashboard/layouts/TableLayout';
import { VERIFICATION_TEMPLATE_TYPE_NAMES_SHORT } from 'dashboard/lib/constants/verification';
import { SortParams } from 'dashboard/lib/interfaces/SortParams';
import {
  VerificationTemplate,
  VerificationTemplateFilterOption,
  VerificationTemplateStatus,
} from 'dashboard/models/verification-template';
import { getEditorPath as getInquiryEditorPath } from 'dashboard/redux/thunk-actions/inquiry-template';
import Sticker from 'design-system/components/Sticker';
import { DATE_AND_TIME_FORMAT, TIMEZONE_NAME_ABBREVIATED } from 'lib/constants/luxonFormats';
import usePermission from 'lib/hooks/usePermission';
import { useVerificationTemplateFullReferenceMap } from 'lib/hooks/useVerificationTemplateReferenceMap';
import { Permission } from 'lib/permissions';

interface VerificationTemplatesTableProps {
  verificationTemplates: VerificationTemplate[];
  verificationTemplateBasics: VerificationTemplateFilterOption[];
  sortParams: SortParams;
  hasUsedFilters: boolean;
  isLoading: boolean;
  handleSortUpdate: (sortParams: SortParams) => void;
}

const VerificationTemplatesTable = ({
  verificationTemplates,
  verificationTemplateBasics,
  sortParams,
  hasUsedFilters,
  isLoading,
  handleSortUpdate,
}: VerificationTemplatesTableProps) => {
  const vtmplRefMap = useVerificationTemplateFullReferenceMap();
  const handleColumnHeaderClick = (columnName: string) => {
    handleSortUpdate({
      sort: columnName,
      shouldSortDescending:
        columnName === sortParams.sort ? !sortParams.shouldSortDescending : true,
    });
  };

  const hasVerificationTemplateViewPermissions = usePermission(Permission.VerificationTemplateView);

  const verificationTemplateRows = useMemo(() => {
    let rows;
    if (!hasVerificationTemplateViewPermissions) {
      return verificationTemplateBasics.map((verificationTemplateBasic) => {
        const { name, status, 'updated-at': updatedAt } = verificationTemplateBasic;
        const updatedAtDisplay = updatedAt
          ? DateTime.fromISO(updatedAt).toFormat(DATE_AND_TIME_FORMAT)
          : '–';

        return (
          <TableTR key={verificationTemplateBasic.id}>
            <TableTD>{name}</TableTD>
            <TableTD>
              <HoverCopyableField
                copyValue={verificationTemplateBasic.id}
                confirmationText='Copied Verification Template ID'
              >
                <MonoSpan>{verificationTemplateBasic.id}</MonoSpan>
              </HoverCopyableField>
            </TableTD>
            <TableTD>
              {VERIFICATION_TEMPLATE_TYPE_NAMES_SHORT[verificationTemplateBasic.type]}
            </TableTD>
            <TableTD>
              <StatusSticker isActive={status === VerificationTemplateStatus.Active} />
            </TableTD>
            <TableTD>{updatedAtDisplay}</TableTD>
          </TableTR>
        );
      });
    } else {
      return verificationTemplates.map((verificationTemplate) => {
        const { name, status, 'updated-at': updatedAt } = verificationTemplate.attributes;
        const updatedAtDisplay = updatedAt
          ? DateTime.fromISO(updatedAt).toFormat(DATE_AND_TIME_FORMAT)
          : '–';

        const { relationships } = verificationTemplate;
        const draftVersionId = relationships['latest-draft-version'].data?.id;
        const publishedVersionId = relationships['latest-published-version'].data?.id;

        // always show draft by default if present
        const templateVersionId = draftVersionId ? draftVersionId : publishedVersionId;

        // display name
        const inquiryTemplate = vtmplRefMap[verificationTemplate.id];
        const inquiryTemplateVersion =
          inquiryTemplate?.relationships?.['latest-draft-version']?.data ||
          inquiryTemplate?.relationships?.['latest-published-version']?.data;

        return (
          <TableLinkTR
            key={verificationTemplate.id}
            id={`verification-templates-table__row--${verificationTemplate.id}`}
            data-test={`verification-templates-table__row--${verificationTemplate.id}`}
            to={`/dashboard/verification-templates/${verificationTemplate.id}/${templateVersionId}`}
          >
            <TableTD>
              {name}{' '}
              {inquiryTemplate && inquiryTemplateVersion && (
                <Link
                  to={getInquiryEditorPath(inquiryTemplate.id, inquiryTemplateVersion.id, 'flow')}
                >
                  <Sticker>{inquiryTemplate.attributes.name}</Sticker>
                </Link>
              )}
            </TableTD>
            <TableTD>
              <HoverCopyableField
                copyValue={verificationTemplate.id}
                confirmationText='Copied Verification Template ID'
              >
                <MonoSpan>{verificationTemplate.id}</MonoSpan>
              </HoverCopyableField>
            </TableTD>
            <TableTD>{VERIFICATION_TEMPLATE_TYPE_NAMES_SHORT[verificationTemplate.type]}</TableTD>
            <TableTD>
              <StatusSticker isActive={status === VerificationTemplateStatus.Active} />
            </TableTD>
            <TableTD>{updatedAtDisplay}</TableTD>
          </TableLinkTR>
        );
      });
    }

    return rows;
  }, [
    verificationTemplates,
    verificationTemplateBasics,
    hasVerificationTemplateViewPermissions,
    vtmplRefMap,
  ]);

  return (
    <TableLayout
      dataTest='verification-templates-table'
      isLoading={isLoading}
      showHeader={true}
      isEmpty={
        !hasVerificationTemplateViewPermissions
          ? verificationTemplateBasics.length === 0
          : verificationTemplates.length === 0
      }
      nullState={
        <TableNullState
          hasUsedFilters={hasUsedFilters}
          dataTestBlock='verification-templates-table__null-state'
        >
          <h3>Looks like there aren&#39;t any verification templates yet.</h3>
        </TableNullState>
      }
      headerTR={
        <>
          <TableTH>Name</TableTH>
          <TableTH>ID</TableTH>
          <TableTH>Type</TableTH>
          <TableTH>Status</TableTH>
          <TableTH
            id='verification-templates-table__udpated-at-header'
            isSorted={sortParams.sort === 'updated_at'}
            isDescending={sortParams.shouldSortDescending}
            onClick={() => handleColumnHeaderClick('updated_at')}
            canSort
          >
            {`Last updated at (${DateTime.local().toFormat(TIMEZONE_NAME_ABBREVIATED)})`}
          </TableTH>
        </>
      }
    >
      {verificationTemplateRows}
    </TableLayout>
  );
};

const StickerContainer = styled('div')`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
`;

const StatusSticker = ({ isActive }: { isActive: boolean }) => {
  return (
    <StickerContainer>
      {isActive ? <Sticker variant='success'>Active</Sticker> : <Sticker>Inactive</Sticker>}
    </StickerContainer>
  );
};

export default VerificationTemplatesTable;
